import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
    Users,
    FileText,
    CreditCard,
    Download,
    LogOut,
    LayoutDashboard,
    Palette,
    Megaphone,
    Mail,
    Settings,
    Gift,
    Ban,
    RefreshCw,
    Plus,
    MessageCircle,
    Tag,
    Newspaper,
    Eye,
} from 'lucide-react';
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    BarChart,
    Bar,
    PieChart as RePieChart,
    Pie,
    Cell,
    Legend,
} from 'recharts';
import { useAdminAuth } from '../../context/AuthContext';
import { adminService } from '../../services/adminService';
import Button from '../ui/Button';

type Tab =
    | 'dashboard'
    | 'visitors'
    | 'users'
    | 'payments'
    | 'support'
    | 'newsletter'
    | 'settings'
    | 'cv-templates'
    | 'cv-pricing'
    | 'cv-announcements'
    | 'cv-emails'
    | 'letter-dashboard'
    | 'letter-templates'
    | 'letter-pricing'
    | 'letter-announcements'
    | 'letter-emails';

type ProductScope = 'cv' | 'letter';

const PIE_COLORS = ['#4f46e5', '#06b6d4', '#f97316', '#22c55e', '#e11d48', '#a855f7', '#64748b'];

function productFromTab(tab: Tab): ProductScope | null {
    if (tab.startsWith('cv-')) return 'cv';
    if (tab.startsWith('letter-')) return 'letter';
    return null;
}

function sectionFromTab(tab: Tab): string {
    if (tab.includes('templates')) return 'templates';
    if (tab.includes('pricing')) return 'pricing';
    if (tab.includes('announcements')) return 'announcements';
    if (tab.includes('emails')) return 'emails';
    return tab;
}

const AdminDashboard: React.FC = () => {
    const { logout, user } = useAdminAuth();
    const [tab, setTab] = useState<Tab>('dashboard');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [stats, setStats] = useState<any>(null);
    const [users, setUsers] = useState<any[]>([]);
    const [payments, setPayments] = useState<any[]>([]);
    const [paymentSummary, setPaymentSummary] = useState<any>(null);
    const [templates, setTemplates] = useState<any[]>([]);
    const [announcements, setAnnouncements] = useState<any[]>([]);
    const [emailLogs, setEmailLogs] = useState<any[]>([]);
    const [settings, setSettings] = useState<any>(null);
    const [tickets, setTickets] = useState<any[]>([]);
    const [selectedTicket, setSelectedTicket] = useState<any>(null);
    const [replyBody, setReplyBody] = useState('');
    const [ticketSourceFilter, setTicketSourceFilter] = useState<'all' | 'contact' | 'report' | 'support'>('all');
    const [newsletterSubs, setNewsletterSubs] = useState<any[]>([]);
    const [newsletterFilter, setNewsletterFilter] = useState<'all' | 'active' | 'unsubscribed'>('all');
    const [userQuery, setUserQuery] = useState('');
    const [selectedUser, setSelectedUser] = useState<any>(null);
    const [creditAmount, setCreditAmount] = useState(1);
    const [paymentFilter, setPaymentFilter] = useState('');
    const [annForm, setAnnForm] = useState({
        title: '',
        body: '',
        type: 'banner',
        audience: 'all',
        product: 'cv' as 'cv' | 'letter' | 'all',
        isActive: true,
        ctaLabel: '',
        ctaUrl: '',
    });
    const [emailForm, setEmailForm] = useState({
        subject: '',
        html: '',
        toAll: true,
        to: '',
        product: 'cv' as 'cv' | 'letter' | 'all',
    });
    const [settingsForm, setSettingsForm] = useState({
        siteName: 'FlashCV',
        smtpHost: '',
        smtpPort: 587,
        smtpSecure: false,
        smtpUser: '',
        smtpPass: '',
        smtpFrom: '',
        downloadPrice: 750,
        letterDownloadPrice: 750,
        paymentMethods: {
            paylink: true,
            wave: true,
            mtn: true,
            orange: true,
            moov: true,
        },
    });
    const [busy, setBusy] = useState(false);
    const [toast, setToast] = useState('');

    const productScope = productFromTab(tab);
    const section = sectionFromTab(tab);
    const productLabel = productScope === 'letter' ? 'Lettre' : productScope === 'cv' ? 'CV' : '';

    const showToast = (msg: string) => {
        setToast(msg);
        setTimeout(() => setToast(''), 3000);
    };

    const loadCore = async () => {
        setLoading(true);
        setError('');
        try {
            const [s, u, p] = await Promise.all([
                adminService.getStatistics(),
                adminService.getUsers(),
                adminService.getPayments(),
            ]);
            setStats(s);
            setUsers(u);
            if (Array.isArray(p)) {
                setPayments(p);
                setPaymentSummary(null);
            } else {
                setPayments(p.payments || []);
                setPaymentSummary(p.summary || null);
            }
        } catch (e: any) {
            setError(e.message || 'Erreur de chargement');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadCore();
    }, []);

    useEffect(() => {
        if (section === 'templates' && productScope) {
            adminService.getTemplates(productScope).then(setTemplates).catch(console.error);
        }
        if (section === 'announcements' && productScope) {
            adminService.getAnnouncements(productScope).then(setAnnouncements).catch(console.error);
            setAnnForm((prev) => ({ ...prev, product: productScope }));
        }
        if (section === 'emails' && productScope) {
            adminService.emailLogs(productScope).then(setEmailLogs).catch(console.error);
            setEmailForm((prev) => ({ ...prev, product: productScope }));
        }
        if (tab === 'support') {
            adminService
                .getSupportTickets()
                .then((list) => {
                    setTickets(list);
                    if (selectedTicket) {
                        const fresh = list.find((t: any) => t._id === selectedTicket._id);
                        if (fresh) setSelectedTicket(fresh);
                    }
                })
                .catch(console.error);
        }
        if (tab === 'newsletter') {
            adminService
                .getNewsletterSubscribers()
                .then(setNewsletterSubs)
                .catch(console.error);
        }
        if (tab === 'settings' || section === 'pricing') {
            adminService
                .getSettings()
                .then((s) => {
                    setSettings(s);
                    setSettingsForm({
                        siteName: s.siteName || 'FlashCV',
                        smtpHost: s.smtpHost || '',
                        smtpPort: s.smtpPort || 587,
                        smtpSecure: !!s.smtpSecure,
                        smtpUser: s.smtpUser || '',
                        smtpPass: '',
                        smtpFrom: s.smtpFrom || '',
                        downloadPrice: Number(s.downloadPrice) > 0 ? Number(s.downloadPrice) : 750,
                        letterDownloadPrice:
                            Number(s.letterDownloadPrice) > 0
                                ? Number(s.letterDownloadPrice)
                                : Number(s.downloadPrice) > 0
                                  ? Number(s.downloadPrice)
                                  : 750,
                        paymentMethods: {
                            paylink: s.paymentMethods?.paylink !== false,
                            wave: s.paymentMethods?.wave !== false,
                            mtn: s.paymentMethods?.mtn !== false,
                            orange: s.paymentMethods?.orange !== false,
                            moov: s.paymentMethods?.moov !== false,
                        },
                    });
                })
                .catch(console.error);
        }
    }, [tab, section, productScope]);

    const totals = stats?.totals || {};
    const filteredUsers = useMemo(() => {
        const q = userQuery.trim().toLowerCase();
        if (!q) return users;
        return users.filter(
            (u) =>
                u.fullName?.toLowerCase().includes(q) ||
                u.email?.toLowerCase().includes(q) ||
                u.phone?.toLowerCase().includes(q)
        );
    }, [users, userQuery]);

    const filteredPayments = useMemo(() => {
        if (!paymentFilter) return payments;
        return payments.filter((p) => p.status === paymentFilter);
    }, [payments, paymentFilter]);

    const navGroups: {
        label?: string;
        items: { id: Tab; label: string; icon: React.ReactNode }[];
    }[] = [
        {
            items: [
                { id: 'dashboard', label: 'Vue d’ensemble', icon: <LayoutDashboard className="w-4 h-4" /> },
                { id: 'visitors', label: 'Visiteurs', icon: <Eye className="w-4 h-4" /> },
                { id: 'users', label: 'Utilisateurs', icon: <Users className="w-4 h-4" /> },
            ],
        },
        {
            label: 'CV',
            items: [
                { id: 'cv-templates', label: 'Modèles CV', icon: <Palette className="w-4 h-4" /> },
                { id: 'cv-pricing', label: 'Prix CV', icon: <Tag className="w-4 h-4" /> },
                { id: 'cv-announcements', label: 'Bannières CV', icon: <Megaphone className="w-4 h-4" /> },
                { id: 'cv-emails', label: 'Emails CV', icon: <Mail className="w-4 h-4" /> },
            ],
        },
        {
            label: 'Lettres',
            items: [
                {
                    id: 'letter-dashboard',
                    label: 'Vue d’ensemble lettres',
                    icon: <LayoutDashboard className="w-4 h-4" />,
                },
                { id: 'letter-templates', label: 'Modèles lettre', icon: <FileText className="w-4 h-4" /> },
                { id: 'letter-pricing', label: 'Prix lettre', icon: <Tag className="w-4 h-4" /> },
                {
                    id: 'letter-announcements',
                    label: 'Bannières lettre',
                    icon: <Megaphone className="w-4 h-4" />,
                },
                { id: 'letter-emails', label: 'Emails lettre', icon: <Mail className="w-4 h-4" /> },
            ],
        },
        {
            items: [
                { id: 'payments', label: 'Paiements', icon: <CreditCard className="w-4 h-4" /> },
                { id: 'support', label: 'Messages', icon: <MessageCircle className="w-4 h-4" /> },
                { id: 'newsletter', label: 'Newsletter', icon: <Newspaper className="w-4 h-4" /> },
                { id: 'settings', label: 'Réglages techniques', icon: <Settings className="w-4 h-4" /> },
            ],
        },
    ];

    const flatNav = navGroups.flatMap((g) => g.items);

    const grant = async () => {
        if (!selectedUser) return;
        setBusy(true);
        try {
            const r = await adminService.grantCredits(selectedUser._id, creditAmount);
            showToast(`${creditAmount} crédit(s) accordé(s)`);
            setSelectedUser({ ...selectedUser, freeDownloadsRemaining: r.freeDownloadsRemaining });
            setUsers((prev) =>
                prev.map((u) =>
                    u._id === selectedUser._id
                        ? { ...u, freeDownloadsRemaining: r.freeDownloadsRemaining }
                        : u
                )
            );
        } catch (e: any) {
            alert(e.message);
        } finally {
            setBusy(false);
        }
    };

    const toggleBlock = async (u: any) => {
        try {
            const updated = await adminService.updateUser(u._id, { isBlocked: !u.isBlocked });
            setUsers((prev) => prev.map((x) => (x._id === u._id ? { ...x, ...updated } : x)));
            if (selectedUser?._id === u._id) setSelectedUser({ ...selectedUser, ...updated });
            showToast(updated.isBlocked ? 'Compte bloqué' : 'Compte débloqué');
        } catch (e: any) {
            alert(e.message);
        }
    };

    return (
        <div className="min-h-screen bg-slate-100 flex">
            <aside className="w-64 bg-slate-900 text-white flex flex-col shrink-0">
                <div className="p-5 border-b border-white/10">
                    <Link to="/" className="text-xl font-black tracking-tight">
                        FlashCV <span className="text-indigo-400">Admin</span>
                    </Link>
                    <p className="text-[10px] text-indigo-300 font-bold uppercase tracking-wider mt-1">
                        Backoffice v2
                    </p>
                    <p className="text-xs text-slate-400 mt-1 truncate">{user?.email}</p>
                </div>
                <nav className="flex-1 p-3 space-y-4 overflow-y-auto">
                    {navGroups.map((group, gi) => (
                        <div key={group.label || `g-${gi}`} className="space-y-1">
                            {group.label && (
                                <p className="px-3 pt-2 text-[10px] font-black uppercase tracking-wider text-indigo-300/80">
                                    {group.label}
                                </p>
                            )}
                            {group.items.map((item) => (
                                <button
                                    key={item.id}
                                    type="button"
                                    onClick={() => setTab(item.id)}
                                    className={`w-full flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm font-semibold transition ${
                                        tab === item.id
                                            ? 'bg-indigo-600 text-white'
                                            : 'text-slate-300 hover:bg-white/5'
                                    }`}
                                >
                                    {item.icon}
                                    {item.label}
                                </button>
                            ))}
                        </div>
                    ))}
                </nav>
                <div className="p-3 border-t border-white/10">
                    <button
                        type="button"
                        onClick={() => {
                            logout();
                            window.location.href = '/bo-flashcv/login';
                        }}
                        className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-300 hover:text-white"
                    >
                        <LogOut className="w-4 h-4" /> Déconnexion
                    </button>
                </div>
            </aside>

            <main className="flex-1 overflow-y-auto">
                <header className="bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between sticky top-0 z-10">
                    <h1 className="text-lg font-black text-slate-800">
                        {flatNav.find((n) => n.id === tab)?.label}
                    </h1>
                    <button
                        type="button"
                        onClick={loadCore}
                        className="flex items-center gap-1.5 text-xs font-semibold text-slate-600 bg-slate-100 px-3 py-1.5 rounded-full hover:bg-slate-200"
                    >
                        <RefreshCw className="w-3.5 h-3.5" /> Actualiser
                    </button>
                </header>

                <div className="p-6 max-w-7xl mx-auto">
                    {toast && (
                        <div className="mb-4 bg-emerald-50 text-emerald-800 text-sm font-semibold px-4 py-2 rounded-lg">
                            {toast}
                        </div>
                    )}
                    {error && (
                        <div className="mb-4 bg-rose-50 text-rose-800 text-sm font-semibold px-4 py-2 rounded-lg">
                            {error}
                        </div>
                    )}
                    {loading && !stats ? (
                        <div className="flex justify-center py-20">
                            <div className="animate-spin rounded-full h-10 w-10 border-2 border-indigo-600 border-t-transparent" />
                        </div>
                    ) : (
                        <>
                            {tab === 'dashboard' && (
                                <div className="space-y-6">
                                    <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
                                        {[
                                            {
                                                title: 'Utilisateurs',
                                                value: totals.users ?? totals.totalUsers ?? 0,
                                                icon: <Users className="w-5 h-5" />,
                                                color: 'bg-indigo-50 text-indigo-600',
                                            },
                                            {
                                                title: 'CV créés',
                                                value: totals.cvs ?? totals.totalCVs ?? 0,
                                                icon: <FileText className="w-5 h-5" />,
                                                color: 'bg-cyan-50 text-cyan-600',
                                            },
                                            {
                                                title: 'Lettres créées',
                                                value: totals.letters ?? totals.totalLetters ?? 0,
                                                icon: <FileText className="w-5 h-5" />,
                                                color: 'bg-teal-50 text-teal-700',
                                            },
                                            {
                                                title: 'Revenus (XOF)',
                                                value: Number(totals.revenue || 0).toLocaleString('fr-FR'),
                                                icon: <CreditCard className="w-5 h-5" />,
                                                color: 'bg-emerald-50 text-emerald-600',
                                            },
                                            {
                                                title: 'Téléchargements',
                                                value: totals.downloads ?? totals.totalDownloads ?? 0,
                                                icon: <Download className="w-5 h-5" />,
                                                color: 'bg-orange-50 text-orange-600',
                                            },
                                        ].map((c) => (
                                            <div key={c.title} className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100">
                                                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${c.color}`}>
                                                    {c.icon}
                                                </div>
                                                <p className="text-xs font-bold uppercase tracking-wider text-slate-400 mt-3">
                                                    {c.title}
                                                </p>
                                                <p className="text-2xl font-black text-slate-900 mt-1">{c.value}</p>
                                            </div>
                                        ))}
                                    </div>
                                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                                        <div className="bg-white rounded-2xl p-4 border border-slate-100 lg:col-span-2">
                                            <p className="text-sm font-bold text-slate-700 mb-3">Inscriptions (30 j)</p>
                                            <div className="h-56">
                                                <ResponsiveContainer width="100%" height="100%">
                                                    <LineChart data={stats?.signupSeries || []}>
                                                        <CartesianGrid strokeDasharray="3 3" />
                                                        <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                                                        <YAxis allowDecimals={false} />
                                                        <Tooltip />
                                                        <Line type="monotone" dataKey="users" stroke="#4f46e5" strokeWidth={2} />
                                                    </LineChart>
                                                </ResponsiveContainer>
                                            </div>
                                        </div>
                                        <div className="bg-white rounded-2xl p-4 border border-slate-100">
                                            <p className="text-sm font-bold text-slate-700 mb-3">Moyens de paiement</p>
                                            <div className="h-56">
                                                <ResponsiveContainer width="100%" height="100%">
                                                    <RePieChart>
                                                        <Pie
                                                            data={stats?.paymentMethods || []}
                                                            dataKey="count"
                                                            nameKey="method"
                                                            outerRadius={70}
                                                            label
                                                        >
                                                            {(stats?.paymentMethods || []).map((_: any, i: number) => (
                                                                <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                                                            ))}
                                                        </Pie>
                                                        <Tooltip />
                                                        <Legend />
                                                    </RePieChart>
                                                </ResponsiveContainer>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                                        <div className="bg-white rounded-2xl p-4 border border-slate-100">
                                            <p className="text-sm font-bold text-slate-700 mb-3">Revenus (30 j)</p>
                                            <div className="h-56">
                                                <ResponsiveContainer width="100%" height="100%">
                                                    <BarChart data={stats?.revenueSeries || []}>
                                                        <CartesianGrid strokeDasharray="3 3" />
                                                        <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                                                        <YAxis />
                                                        <Tooltip />
                                                        <Bar dataKey="revenue" fill="#10b981" radius={[4, 4, 0, 0]} />
                                                    </BarChart>
                                                </ResponsiveContainer>
                                            </div>
                                        </div>
                                        <div className="bg-white rounded-2xl p-4 border border-slate-100">
                                            <p className="text-sm font-bold text-slate-700 mb-3">Top modèles utilisés</p>
                                            <div className="h-56">
                                                <ResponsiveContainer width="100%" height="100%">
                                                    <BarChart data={stats?.templateStats || []} layout="vertical">
                                                        <CartesianGrid strokeDasharray="3 3" />
                                                        <XAxis type="number" allowDecimals={false} />
                                                        <YAxis type="category" dataKey="name" width={100} tick={{ fontSize: 10 }} />
                                                        <Tooltip />
                                                        <Bar dataKey="count" fill="#6366f1" radius={[0, 4, 4, 0]} />
                                                    </BarChart>
                                                </ResponsiveContainer>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-2 gap-4">
                                        <div className="bg-white rounded-2xl p-4 border border-slate-100">
                                            <p className="text-xs text-slate-500 font-bold uppercase">Crédits actifs</p>
                                            <p className="text-xl font-black">{totals.usersWithCredits || 0} users</p>
                                        </div>
                                        <div className="bg-white rounded-2xl p-4 border border-slate-100">
                                            <p className="text-xs text-slate-500 font-bold uppercase">Modèles ouverts</p>
                                            <p className="text-xl font-black">{totals.activeTemplates || 0}</p>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {tab === 'visitors' && (
                                <div className="space-y-6">
                                    <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
                                        {[
                                            {
                                                title: 'Visiteurs aujourd’hui',
                                                value: totals.visitorsToday ?? 0,
                                                icon: <Eye className="w-5 h-5" />,
                                                color: 'bg-indigo-50 text-indigo-600',
                                            },
                                            {
                                                title: 'Visiteurs hier',
                                                value: totals.visitorsYesterday ?? 0,
                                                icon: <Eye className="w-5 h-5" />,
                                                color: 'bg-cyan-50 text-cyan-600',
                                            },
                                            {
                                                title: 'Visiteurs (30 derniers jours)',
                                                value: (stats?.visitSeries || []).reduce(
                                                    (acc: number, r: { visitors: number }) => acc + r.visitors,
                                                    0
                                                ),
                                                icon: <Eye className="w-5 h-5" />,
                                                color: 'bg-emerald-50 text-emerald-600',
                                            },
                                        ].map((c) => (
                                            <div key={c.title} className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100">
                                                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${c.color}`}>
                                                    {c.icon}
                                                </div>
                                                <p className="text-xs font-bold uppercase tracking-wider text-slate-400 mt-3">
                                                    {c.title}
                                                </p>
                                                <p className="text-2xl font-black text-slate-900 mt-1">{c.value}</p>
                                            </div>
                                        ))}
                                    </div>
                                    <div className="bg-white rounded-2xl p-4 border border-slate-100">
                                        <p className="text-sm font-bold text-slate-700 mb-3">Visiteurs uniques par jour (30 j)</p>
                                        <div className="h-72">
                                            <ResponsiveContainer width="100%" height="100%">
                                                <LineChart data={stats?.visitSeries || []}>
                                                    <CartesianGrid strokeDasharray="3 3" />
                                                    <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                                                    <YAxis allowDecimals={false} />
                                                    <Tooltip />
                                                    <Line type="monotone" dataKey="visitors" stroke="#4f46e5" strokeWidth={2} />
                                                </LineChart>
                                            </ResponsiveContainer>
                                        </div>
                                        <p className="text-xs text-slate-400 mt-3">
                                            Un visiteur compte une fois par jour maximum (identifiant anonyme stocké dans le
                                            navigateur, aucune donnée personnelle). Les visites sur le backoffice ne sont pas comptabilisées.
                                        </p>
                                    </div>
                                </div>
                            )}

                            {tab === 'letter-dashboard' && (
                                <div className="space-y-6">
                                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                                        {[
                                            {
                                                title: 'Lettres créées',
                                                value: totals.letters ?? totals.totalLetters ?? 0,
                                                icon: <FileText className="w-5 h-5" />,
                                                color: 'bg-teal-50 text-teal-700',
                                            },
                                            {
                                                title: 'Revenus lettres (XOF)',
                                                value: Number(totals.letterRevenue || 0).toLocaleString(
                                                    'fr-FR'
                                                ),
                                                icon: <CreditCard className="w-5 h-5" />,
                                                color: 'bg-emerald-50 text-emerald-600',
                                            },
                                            {
                                                title: 'Téléchargements lettres',
                                                value: totals.letterDownloads ?? 0,
                                                icon: <Download className="w-5 h-5" />,
                                                color: 'bg-orange-50 text-orange-600',
                                            },
                                            {
                                                title: 'Modèles lettre ouverts',
                                                value: totals.activeLetterTemplates ?? 0,
                                                icon: <Palette className="w-5 h-5" />,
                                                color: 'bg-indigo-50 text-indigo-600',
                                            },
                                        ].map((c) => (
                                            <div
                                                key={c.title}
                                                className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100"
                                            >
                                                <div
                                                    className={`w-10 h-10 rounded-xl flex items-center justify-center ${c.color}`}
                                                >
                                                    {c.icon}
                                                </div>
                                                <p className="text-xs font-bold uppercase tracking-wider text-slate-400 mt-3">
                                                    {c.title}
                                                </p>
                                                <p className="text-2xl font-black text-slate-900 mt-1">
                                                    {c.value}
                                                </p>
                                            </div>
                                        ))}
                                    </div>
                                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                                        <div className="bg-white rounded-2xl p-4 border border-slate-100 lg:col-span-2">
                                            <p className="text-sm font-bold text-slate-700 mb-3">
                                                Lettres créées (30 j)
                                            </p>
                                            <div className="h-56">
                                                <ResponsiveContainer width="100%" height="100%">
                                                    <LineChart data={stats?.letterCreationSeries || []}>
                                                        <CartesianGrid strokeDasharray="3 3" />
                                                        <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                                                        <YAxis allowDecimals={false} />
                                                        <Tooltip />
                                                        <Line
                                                            type="monotone"
                                                            dataKey="letters"
                                                            stroke="#0d9488"
                                                            strokeWidth={2}
                                                        />
                                                    </LineChart>
                                                </ResponsiveContainer>
                                            </div>
                                        </div>
                                        <div className="bg-white rounded-2xl p-4 border border-slate-100">
                                            <p className="text-sm font-bold text-slate-700 mb-3">
                                                Paiements lettres
                                            </p>
                                            <div className="h-56">
                                                <ResponsiveContainer width="100%" height="100%">
                                                    <RePieChart>
                                                        <Pie
                                                            data={stats?.letterPaymentMethods || []}
                                                            dataKey="count"
                                                            nameKey="method"
                                                            outerRadius={70}
                                                            label
                                                        >
                                                            {(stats?.letterPaymentMethods || []).map(
                                                                (_: any, i: number) => (
                                                                    <Cell
                                                                        key={i}
                                                                        fill={PIE_COLORS[i % PIE_COLORS.length]}
                                                                    />
                                                                )
                                                            )}
                                                        </Pie>
                                                        <Tooltip />
                                                        <Legend />
                                                    </RePieChart>
                                                </ResponsiveContainer>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                                        <div className="bg-white rounded-2xl p-4 border border-slate-100">
                                            <p className="text-sm font-bold text-slate-700 mb-3">
                                                Revenus lettres (30 j)
                                            </p>
                                            <div className="h-56">
                                                <ResponsiveContainer width="100%" height="100%">
                                                    <BarChart data={stats?.letterRevenueSeries || []}>
                                                        <CartesianGrid strokeDasharray="3 3" />
                                                        <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                                                        <YAxis />
                                                        <Tooltip />
                                                        <Bar
                                                            dataKey="revenue"
                                                            fill="#0d9488"
                                                            radius={[4, 4, 0, 0]}
                                                        />
                                                    </BarChart>
                                                </ResponsiveContainer>
                                            </div>
                                        </div>
                                        <div className="bg-white rounded-2xl p-4 border border-slate-100">
                                            <p className="text-sm font-bold text-slate-700 mb-3">
                                                Top modèles lettre
                                            </p>
                                            <div className="h-56">
                                                <ResponsiveContainer width="100%" height="100%">
                                                    <BarChart
                                                        data={stats?.letterTemplateStats || []}
                                                        layout="vertical"
                                                    >
                                                        <CartesianGrid strokeDasharray="3 3" />
                                                        <XAxis type="number" allowDecimals={false} />
                                                        <YAxis
                                                            type="category"
                                                            dataKey="name"
                                                            width={110}
                                                            tick={{ fontSize: 10 }}
                                                        />
                                                        <Tooltip />
                                                        <Bar
                                                            dataKey="count"
                                                            fill="#14b8a6"
                                                            radius={[0, 4, 4, 0]}
                                                        />
                                                    </BarChart>
                                                </ResponsiveContainer>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {tab === 'users' && (
                                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                                    <div className="lg:col-span-2 bg-white rounded-2xl border border-slate-100 overflow-hidden">
                                        <div className="p-4 border-b">
                                            <input
                                                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
                                                placeholder="Rechercher nom, email…"
                                                value={userQuery}
                                                onChange={(e) => setUserQuery(e.target.value)}
                                            />
                                        </div>
                                        <div className="max-h-[70vh] overflow-y-auto">
                                            <table className="w-full text-sm">
                                                <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500">
                                                    <tr>
                                                        <th className="px-4 py-2">Utilisateur</th>
                                                        <th className="px-4 py-2">CV</th>
                                                        <th className="px-4 py-2">LM</th>
                                                        <th className="px-4 py-2">Revenu</th>
                                                        <th className="px-4 py-2">Crédits</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {filteredUsers.map((u) => (
                                                        <tr
                                                            key={u._id}
                                                            onClick={() => setSelectedUser(u)}
                                                            className={`border-t cursor-pointer hover:bg-indigo-50/50 ${
                                                                selectedUser?._id === u._id ? 'bg-indigo-50' : ''
                                                            }`}
                                                        >
                                                            <td className="px-4 py-3">
                                                                <p className="font-bold text-slate-800">{u.fullName}</p>
                                                                <p className="text-xs text-slate-500">{u.email}</p>
                                                                {u.isBlocked && (
                                                                    <span className="text-[10px] font-bold text-rose-600">BLOQUÉ</span>
                                                                )}
                                                            </td>
                                                            <td className="px-4 py-3">{u.cvCount || 0}</td>
                                                            <td className="px-4 py-3">{u.letterCount || 0}</td>
                                                            <td className="px-4 py-3">{(u.revenue || 0).toLocaleString('fr-FR')}</td>
                                                            <td className="px-4 py-3 font-bold text-indigo-600">
                                                                {u.freeDownloadsRemaining || 0}
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                    <div className="bg-white rounded-2xl border border-slate-100 p-5 h-fit sticky top-24">
                                        {selectedUser ? (
                                            <div className="space-y-4">
                                                <div>
                                                    <p className="text-lg font-black">{selectedUser.fullName}</p>
                                                    <p className="text-sm text-slate-500">{selectedUser.email}</p>
                                                </div>
                                                <p className="text-sm">
                                                    CV : <strong>{selectedUser.cvCount || 0}</strong>
                                                    {' · '}
                                                    LM : <strong>{selectedUser.letterCount || 0}</strong>
                                                </p>
                                                <p className="text-sm">
                                                    Crédits restants :{' '}
                                                    <strong>{selectedUser.freeDownloadsRemaining || 0}</strong>
                                                </p>
                                                <div className="flex gap-2 items-end">
                                                    <div className="flex-1">
                                                        <label className="text-xs font-bold text-slate-500">Accorder</label>
                                                        <input
                                                            type="number"
                                                            min={1}
                                                            value={creditAmount}
                                                            onChange={(e) => setCreditAmount(Number(e.target.value) || 1)}
                                                            className="w-full border rounded-lg px-3 py-2 text-sm"
                                                        />
                                                    </div>
                                                    <Button onClick={grant} disabled={busy} className="flex items-center gap-1">
                                                        <Gift className="w-4 h-4" /> OK
                                                    </Button>
                                                </div>
                                                <button
                                                    type="button"
                                                    onClick={() => toggleBlock(selectedUser)}
                                                    className="w-full flex items-center justify-center gap-2 text-sm font-semibold border rounded-lg py-2 hover:bg-slate-50"
                                                >
                                                    <Ban className="w-4 h-4" />
                                                    {selectedUser.isBlocked ? 'Débloquer' : 'Bloquer'}
                                                </button>
                                                <textarea
                                                    className="w-full border rounded-lg px-3 py-2 text-sm min-h-[80px]"
                                                    placeholder="Notes admin…"
                                                    defaultValue={selectedUser.notes || ''}
                                                    onBlur={async (e) => {
                                                        try {
                                                            await adminService.updateUser(selectedUser._id, {
                                                                notes: e.target.value,
                                                            });
                                                            showToast('Notes enregistrées');
                                                        } catch (err: any) {
                                                            alert(err.message);
                                                        }
                                                    }}
                                                />
                                            </div>
                                        ) : (
                                            <p className="text-sm text-slate-500">Sélectionnez un utilisateur</p>
                                        )}
                                    </div>
                                </div>
                            )}

                            {section === 'templates' && (
                                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                                    {templates.map((t) => (
                                        <div
                                            key={t._id}
                                            className="bg-white rounded-2xl border border-slate-100 p-5 flex flex-col gap-3"
                                        >
                                            <div className="flex items-start justify-between gap-2">
                                                <div>
                                                    <p className="font-black text-slate-900">{t.name}</p>
                                                    <p className="text-xs text-slate-400 font-mono">{t.slug}</p>
                                                </div>
                                                <div
                                                    className="w-8 h-8 rounded-full border"
                                                    style={{ backgroundColor: t.hex || '#ccc' }}
                                                />
                                            </div>
                                            <div className="flex flex-wrap gap-2">
                                                {t.isNewBadge && (
                                                    <span className="text-[10px] font-black uppercase bg-rose-100 text-rose-700 px-2 py-0.5 rounded-full">
                                                        New
                                                    </span>
                                                )}
                                                <span
                                                    className={`text-[10px] font-black uppercase px-2 py-0.5 rounded-full ${
                                                        t.isActive
                                                            ? 'bg-emerald-100 text-emerald-700'
                                                            : 'bg-slate-100 text-slate-500'
                                                    }`}
                                                >
                                                    {t.isActive ? 'Ouvert' : 'Fermé'}
                                                </span>
                                            </div>
                                            <div className="flex gap-2 mt-auto">
                                                <button
                                                    type="button"
                                                    onClick={async () => {
                                                        const u = await adminService.toggleTemplate(t._id);
                                                        setTemplates((prev) =>
                                                            prev.map((x) => (x._id === t._id ? u : x))
                                                        );
                                                    }}
                                                    className="flex-1 text-xs font-bold border rounded-lg py-2 hover:bg-slate-50"
                                                >
                                                    {t.isActive ? 'Fermer' : 'Ouvrir'}
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={async () => {
                                                        const u = await adminService.updateTemplate(t._id, {
                                                            isNewBadge: !t.isNewBadge,
                                                        });
                                                        setTemplates((prev) =>
                                                            prev.map((x) => (x._id === t._id ? u : x))
                                                        );
                                                    }}
                                                    className="flex-1 text-xs font-bold border rounded-lg py-2 hover:bg-slate-50"
                                                >
                                                    {t.isNewBadge ? 'Retirer NEW' : 'Marquer NEW'}
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {tab === 'payments' && (
                                <div className="space-y-4">
                                    {paymentSummary && (
                                        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                                            {(
                                                [
                                                    ['completed', 'Encaissés', 'text-emerald-700 bg-emerald-50'],
                                                    ['pending', 'En attente', 'text-amber-700 bg-amber-50'],
                                                    ['failed', 'Échoués', 'text-rose-700 bg-rose-50'],
                                                    ['refunded', 'Remboursés', 'text-slate-700 bg-slate-100'],
                                                ] as const
                                            ).map(([key, label, cls]) => (
                                                <div key={key} className={`rounded-2xl border p-4 ${cls}`}>
                                                    <p className="text-xs font-bold uppercase opacity-70">{label}</p>
                                                    <p className="text-xl font-black mt-1">
                                                        {(paymentSummary[key]?.total || 0).toLocaleString('fr-FR')} XOF
                                                    </p>
                                                    <p className="text-xs mt-0.5">
                                                        {paymentSummary[key]?.count || 0} transaction
                                                        {(paymentSummary[key]?.count || 0) > 1 ? 's' : ''}
                                                    </p>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                    <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
                                    <div className="p-4 border-b flex gap-2 flex-wrap items-center">
                                        {['', 'completed', 'pending', 'failed', 'refunded'].map((s) => (
                                            <button
                                                key={s || 'all'}
                                                type="button"
                                                onClick={() => setPaymentFilter(s)}
                                                className={`text-xs font-bold px-3 py-1.5 rounded-full ${
                                                    paymentFilter === s
                                                        ? 'bg-indigo-600 text-white'
                                                        : 'bg-slate-100 text-slate-600'
                                                }`}
                                            >
                                                {s || 'tous'}
                                            </button>
                                        ))}
                                        <button
                                            type="button"
                                            className="ml-auto text-xs font-bold px-3 py-1.5 rounded-full bg-emerald-600 text-white hover:bg-emerald-700"
                                            onClick={async () => {
                                                try {
                                                    setBusy(true);
                                                    const r = await adminService.reconcilePayments();
                                                    const done = (r.results || []).filter(
                                                        (x: any) => x.status === 'completed'
                                                    ).length;
                                                    showToast(
                                                        `Sync Nafolo : ${done} confirmé(s) / ${(r.results || []).length} vérifié(s)`
                                                    );
                                                    await loadCore();
                                                } catch (e: any) {
                                                    alert(e.message);
                                                } finally {
                                                    setBusy(false);
                                                }
                                            }}
                                            disabled={busy}
                                        >
                                            Synchroniser avec Nafolo
                                        </button>
                                    </div>
                                    <div className="overflow-x-auto max-h-[70vh]">
                                        <table className="w-full text-sm">
                                            <thead className="bg-slate-50 text-xs uppercase text-slate-500 text-left">
                                                <tr>
                                                    <th className="px-4 py-2">Date</th>
                                                    <th className="px-4 py-2">Qui</th>
                                                    <th className="px-4 py-2">Montant</th>
                                                    <th className="px-4 py-2">Moyen</th>
                                                    <th className="px-4 py-2">Statut</th>
                                                    <th className="px-4 py-2">TXN FlashCV</th>
                                                    <th className="px-4 py-2">ID Nafolo</th>
                                                    <th className="px-4 py-2">CV</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {filteredPayments.map((p) => (
                                                    <tr key={p._id} className="border-t align-top">
                                                        <td className="px-4 py-2 whitespace-nowrap text-xs">
                                                            {new Date(p.createdAt).toLocaleString('fr-FR')}
                                                            {p.completedAt && (
                                                                <div className="text-slate-400">
                                                                    OK {new Date(p.completedAt).toLocaleString('fr-FR')}
                                                                </div>
                                                            )}
                                                        </td>
                                                        <td className="px-4 py-2">
                                                            <div className="font-semibold">{p.userId?.fullName || '—'}</div>
                                                            <div className="text-xs text-slate-400">{p.userId?.email}</div>
                                                            {(p.metadata?.phoneNumber || p.userId?.phone) && (
                                                                <div className="text-xs text-slate-500">
                                                                    {p.metadata?.phoneNumber || p.userId?.phone}
                                                                </div>
                                                            )}
                                                        </td>
                                                        <td className="px-4 py-2 font-bold whitespace-nowrap">
                                                            {p.amount?.toLocaleString('fr-FR')} {p.currency || 'XOF'}
                                                        </td>
                                                        <td className="px-4 py-2 uppercase text-xs font-bold">
                                                            {p.paymentMethod}
                                                            {p.metadata?.mode && (
                                                                <div className="normal-case font-normal text-slate-400">
                                                                    {p.metadata.mode}
                                                                </div>
                                                            )}
                                                        </td>
                                                        <td className="px-4 py-2">
                                                            <span
                                                                className={`text-xs font-bold uppercase px-2 py-0.5 rounded-full ${
                                                                    p.status === 'completed'
                                                                        ? 'bg-emerald-100 text-emerald-800'
                                                                        : p.status === 'pending'
                                                                          ? 'bg-amber-100 text-amber-800'
                                                                          : p.status === 'failed'
                                                                            ? 'bg-rose-100 text-rose-800'
                                                                            : 'bg-slate-100 text-slate-700'
                                                                }`}
                                                            >
                                                                {p.status}
                                                            </span>
                                                        </td>
                                                        <td className="px-4 py-2 text-[11px] font-mono max-w-[140px] break-all">
                                                            {p.transactionId || '—'}
                                                        </td>
                                                        <td className="px-4 py-2 text-[11px] font-mono max-w-[160px] break-all">
                                                            {p.metadata?.nafoloTransactionId ||
                                                                p.metadata?.paylinkId ||
                                                                p.metadata?.signatureKey ||
                                                                p.metadata?.externalId ||
                                                                '—'}
                                                        </td>
                                                        <td className="px-4 py-2 text-xs">
                                                            {p.cvId?.title || p.cvId?.template || String(p.cvId || '—')}
                                                        </td>
                                                    </tr>
                                                ))}
                                                {filteredPayments.length === 0 && (
                                                    <tr>
                                                        <td colSpan={8} className="px-4 py-8 text-center text-slate-400">
                                                            Aucun paiement
                                                        </td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                    </div>
                                </div>
                            )}

                            {section === 'announcements' && (
                                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                                    <div className="bg-white rounded-2xl border p-5 space-y-3">
                                        <p className="font-black flex items-center gap-2">
                                            <Plus className="w-4 h-4" /> Nouvelle annonce {productLabel}
                                        </p>
                                        <input
                                            className="w-full border rounded-lg px-3 py-2 text-sm"
                                            placeholder="Titre"
                                            value={annForm.title}
                                            onChange={(e) => setAnnForm({ ...annForm, title: e.target.value })}
                                        />
                                        <textarea
                                            className="w-full border rounded-lg px-3 py-2 text-sm min-h-[100px]"
                                            placeholder="Message"
                                            value={annForm.body}
                                            onChange={(e) => setAnnForm({ ...annForm, body: e.target.value })}
                                        />
                                        <div className="flex gap-2">
                                            <select
                                                className="border rounded-lg px-3 py-2 text-sm flex-1"
                                                value={annForm.type}
                                                onChange={(e) => setAnnForm({ ...annForm, type: e.target.value })}
                                            >
                                                <option value="banner">Bannière</option>
                                                <option value="popup">Popup</option>
                                            </select>
                                            <select
                                                className="border rounded-lg px-3 py-2 text-sm flex-1"
                                                value={annForm.audience}
                                                onChange={(e) => setAnnForm({ ...annForm, audience: e.target.value })}
                                            >
                                                <option value="all">Tous</option>
                                                <option value="users">Connectés</option>
                                                <option value="guests">Invités</option>
                                            </select>
                                        </div>
                                        <input
                                            className="w-full border rounded-lg px-3 py-2 text-sm"
                                            placeholder="CTA label (optionnel)"
                                            value={annForm.ctaLabel}
                                            onChange={(e) => setAnnForm({ ...annForm, ctaLabel: e.target.value })}
                                        />
                                        <input
                                            className="w-full border rounded-lg px-3 py-2 text-sm"
                                            placeholder="CTA URL"
                                            value={annForm.ctaUrl}
                                            onChange={(e) => setAnnForm({ ...annForm, ctaUrl: e.target.value })}
                                        />
                                        <Button
                                            onClick={async () => {
                                                try {
                                                    await adminService.createAnnouncement({
                                                        ...annForm,
                                                        product: productScope || annForm.product,
                                                    });
                                                    setAnnouncements(
                                                        await adminService.getAnnouncements(
                                                            productScope || undefined
                                                        )
                                                    );
                                                    setAnnForm({
                                                        title: '',
                                                        body: '',
                                                        type: 'banner',
                                                        audience: 'all',
                                                        product: productScope || 'cv',
                                                        isActive: true,
                                                        ctaLabel: '',
                                                        ctaUrl: '',
                                                    });
                                                    showToast(`Annonce ${productLabel} créée`);
                                                } catch (e: any) {
                                                    alert(e.message);
                                                }
                                            }}
                                        >
                                            Publier
                                        </Button>
                                    </div>
                                    <div className="space-y-3">
                                        {announcements.map((a) => (
                                            <div key={a._id} className="bg-white rounded-2xl border p-4">
                                                <div className="flex justify-between gap-2">
                                                    <div>
                                                        <p className="font-bold">{a.title}</p>
                                                        <p className="text-sm text-slate-600 mt-1">{a.body}</p>
                                                        <p className="text-[10px] uppercase font-bold text-slate-400 mt-2">
                                                            {a.type} · {a.audience} · {a.isActive ? 'actif' : 'off'}
                                                        </p>
                                                    </div>
                                                    <div className="flex flex-col gap-1">
                                                        <button
                                                            type="button"
                                                            className="text-xs font-bold border rounded px-2 py-1"
                                                            onClick={async () => {
                                                                await adminService.updateAnnouncement(a._id, {
                                                                    isActive: !a.isActive,
                                                                });
                                                                setAnnouncements(
                                                                    await adminService.getAnnouncements(
                                                                        productScope || undefined
                                                                    )
                                                                );
                                                            }}
                                                        >
                                                            {a.isActive ? 'Désactiver' : 'Activer'}
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className="text-xs font-bold text-rose-600 border border-rose-200 rounded px-2 py-1"
                                                            onClick={async () => {
                                                                await adminService.deleteAnnouncement(a._id);
                                                                setAnnouncements(
                                                                    await adminService.getAnnouncements(
                                                                        productScope || undefined
                                                                    )
                                                                );
                                                            }}
                                                        >
                                                            Supprimer
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {section === 'emails' && (
                                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                                    <div className="bg-white rounded-2xl border p-5 space-y-3">
                                        <p className="font-black">Envoyer un email {productLabel}</p>
                                        <p className="text-xs text-slate-500 -mt-1">
                                            Cet envoi est étiqueté « {productLabel} » (séparé de l’autre produit).
                                        </p>
                                        <input
                                            className="w-full border rounded-lg px-3 py-2 text-sm"
                                            placeholder="Sujet"
                                            value={emailForm.subject}
                                            onChange={(e) => setEmailForm({ ...emailForm, subject: e.target.value })}
                                        />
                                        <textarea
                                            className="w-full border rounded-lg px-3 py-2 text-sm min-h-[140px]"
                                            placeholder="Contenu (HTML ou texte)"
                                            value={emailForm.html}
                                            onChange={(e) => setEmailForm({ ...emailForm, html: e.target.value })}
                                        />
                                        <label className="flex items-center gap-2 text-sm">
                                            <input
                                                type="checkbox"
                                                checked={emailForm.toAll}
                                                onChange={(e) =>
                                                    setEmailForm({ ...emailForm, toAll: e.target.checked })
                                                }
                                            />
                                            Envoyer à tous les utilisateurs
                                        </label>
                                        {!emailForm.toAll && (
                                            <input
                                                className="w-full border rounded-lg px-3 py-2 text-sm"
                                                placeholder="Email destinataire"
                                                value={emailForm.to}
                                                onChange={(e) => setEmailForm({ ...emailForm, to: e.target.value })}
                                            />
                                        )}
                                        <Button
                                            onClick={async () => {
                                                try {
                                                    const r = await adminService.sendEmail({
                                                        subject: emailForm.subject,
                                                        html: emailForm.html,
                                                        text: emailForm.html,
                                                        toAll: emailForm.toAll,
                                                        to: emailForm.toAll ? undefined : emailForm.to,
                                                        product: productScope || emailForm.product,
                                                    });
                                                    showToast(`Envoyés: ${r.sent}, échecs: ${r.failed}`);
                                                    setEmailLogs(
                                                        await adminService.emailLogs(productScope || undefined)
                                                    );
                                                } catch (e: any) {
                                                    alert(e.message);
                                                }
                                            }}
                                        >
                                            Envoyer
                                        </Button>
                                    </div>
                                    <div className="bg-white rounded-2xl border p-5">
                                        <p className="font-black mb-3">Historique</p>
                                        <div className="space-y-2 max-h-[60vh] overflow-y-auto">
                                            {emailLogs.map((l) => (
                                                <div key={l._id} className="text-sm border-b py-2">
                                                    <div className="flex justify-between">
                                                        <span className="font-bold">{l.subject}</span>
                                                        <span
                                                            className={`text-xs font-bold ${
                                                                l.status === 'sent' ? 'text-emerald-600' : 'text-rose-600'
                                                            }`}
                                                        >
                                                            {l.status}
                                                        </span>
                                                    </div>
                                                    <p className="text-xs text-slate-500">{l.to}</p>
                                                    <p className="text-[10px] text-slate-400">
                                                        {new Date(l.sentAt).toLocaleString('fr-FR')}
                                                    </p>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {section === 'pricing' && productScope && (
                                <div className="max-w-lg bg-white rounded-2xl border p-6 space-y-4">
                                    <div>
                                        <p className="font-black">
                                            Prix téléchargement {productLabel}
                                        </p>
                                        <p className="text-xs text-slate-500 mt-1">
                                            Montant facturé pour débloquer le PDF{' '}
                                            {productScope === 'letter'
                                                ? 'de la lettre de motivation'
                                                : 'du CV'}
                                            . Indépendant de l’autre produit.
                                        </p>
                                    </div>
                                    <div>
                                        <label className="text-xs font-bold text-slate-500">
                                            Prix à payer (FCFA / XOF)
                                        </label>
                                        <div className="flex items-center gap-2 mt-1">
                                            <input
                                                type="number"
                                                min={1}
                                                step={50}
                                                className="w-40 border rounded-lg px-3 py-2 text-sm font-mono font-bold"
                                                value={
                                                    productScope === 'letter'
                                                        ? settingsForm.letterDownloadPrice
                                                        : settingsForm.downloadPrice
                                                }
                                                onChange={(e) => {
                                                    const n = Number(e.target.value) || 0;
                                                    setSettingsForm((prev) =>
                                                        productScope === 'letter'
                                                            ? { ...prev, letterDownloadPrice: n }
                                                            : { ...prev, downloadPrice: n }
                                                    );
                                                }}
                                            />
                                            <span className="text-sm font-semibold text-slate-600">FCFA</span>
                                        </div>
                                    </div>
                                    <Button
                                        onClick={async () => {
                                            try {
                                                const price =
                                                    productScope === 'letter'
                                                        ? settingsForm.letterDownloadPrice
                                                        : settingsForm.downloadPrice;
                                                if (!price || price < 1) {
                                                    alert('Indiquez un prix ≥ 1 FCFA.');
                                                    return;
                                                }
                                                const payload =
                                                    productScope === 'letter'
                                                        ? { letterDownloadPrice: price }
                                                        : { downloadPrice: price };
                                                const s = await adminService.updateSettings(payload);
                                                setSettings(s);
                                                setSettingsForm((prev) => ({
                                                    ...prev,
                                                    downloadPrice:
                                                        Number(s.downloadPrice) > 0
                                                            ? Number(s.downloadPrice)
                                                            : prev.downloadPrice,
                                                    letterDownloadPrice:
                                                        Number(s.letterDownloadPrice) > 0
                                                            ? Number(s.letterDownloadPrice)
                                                            : prev.letterDownloadPrice,
                                                }));
                                                const saved =
                                                    productScope === 'letter'
                                                        ? s.letterDownloadPrice
                                                        : s.downloadPrice;
                                                showToast(
                                                    `Prix ${productLabel} : ${saved} FCFA`
                                                );
                                            } catch (e: any) {
                                                alert(e.message);
                                            }
                                        }}
                                    >
                                        Enregistrer le prix {productLabel}
                                    </Button>
                                </div>
                            )}

                            {tab === 'support' && (
                                <div className="space-y-4">
                                    <div className="flex flex-wrap gap-2">
                                        {(
                                            [
                                                { id: 'all', label: 'Tous' },
                                                { id: 'contact', label: 'Contact site' },
                                                { id: 'report', label: 'Signalements' },
                                                { id: 'support', label: 'Support' },
                                            ] as const
                                        ).map((f) => (
                                            <button
                                                key={f.id}
                                                type="button"
                                                onClick={() => {
                                                    setTicketSourceFilter(f.id);
                                                    setSelectedTicket(null);
                                                }}
                                                className={`text-xs font-bold px-3 py-1.5 rounded-full border ${
                                                    ticketSourceFilter === f.id
                                                        ? 'bg-indigo-600 text-white border-indigo-600'
                                                        : 'bg-white text-slate-600 border-slate-200 hover:border-indigo-300'
                                                }`}
                                            >
                                                {f.label}
                                                <span className="ml-1 opacity-70">
                                                    (
                                                    {
                                                        tickets.filter((t) =>
                                                            f.id === 'all'
                                                                ? true
                                                                : (t.source || 'support') === f.id
                                                        ).length
                                                    }
                                                    )
                                                </span>
                                            </button>
                                        ))}
                                    </div>
                                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                                    <div className="lg:col-span-1 bg-white rounded-2xl border overflow-hidden max-h-[75vh] overflow-y-auto">
                                        {tickets.filter((t) =>
                                            ticketSourceFilter === 'all'
                                                ? true
                                                : (t.source || 'support') === ticketSourceFilter
                                        ).length === 0 && (
                                            <p className="p-4 text-sm text-slate-500">Aucun message pour le moment.</p>
                                        )}
                                        {tickets
                                            .filter((t) =>
                                                ticketSourceFilter === 'all'
                                                    ? true
                                                    : (t.source || 'support') === ticketSourceFilter
                                            )
                                            .map((t) => (
                                            <button
                                                key={t._id}
                                                type="button"
                                                onClick={() => {
                                                    setSelectedTicket(t);
                                                    setReplyBody('');
                                                }}
                                                className={`w-full text-left px-4 py-3 border-b hover:bg-slate-50 ${
                                                    selectedTicket?._id === t._id ? 'bg-indigo-50' : ''
                                                }`}
                                            >
                                                <div className="flex justify-between gap-2 items-start">
                                                    <p className="font-bold text-sm text-slate-900 truncate">{t.subject}</p>
                                                    <span className="text-[10px] font-black uppercase text-slate-400 shrink-0">
                                                        {t.status}
                                                    </span>
                                                </div>
                                                <span
                                                    className={`inline-block mt-1 text-[10px] font-bold uppercase px-1.5 py-0.5 rounded ${
                                                        t.source === 'contact'
                                                            ? 'bg-sky-100 text-sky-700'
                                                            : t.source === 'report'
                                                              ? 'bg-amber-100 text-amber-800'
                                                              : 'bg-slate-100 text-slate-600'
                                                    }`}
                                                >
                                                    {t.source === 'contact'
                                                        ? 'Contact'
                                                        : t.source === 'report'
                                                          ? 'Signalement'
                                                          : 'Support'}
                                                </span>
                                                <p className="text-xs text-slate-500 mt-0.5">
                                                    {t.userId?.fullName || t.guestName || 'Anonyme'} ·{' '}
                                                    {t.userId?.email || t.guestEmail}
                                                </p>
                                                <p className="text-[10px] text-slate-400 mt-1">
                                                    {new Date(t.updatedAt || t.createdAt).toLocaleString('fr-FR')}
                                                </p>
                                            </button>
                                        ))}
                                    </div>
                                    <div className="lg:col-span-2 bg-white rounded-2xl border p-5 min-h-[320px]">
                                        {!selectedTicket ? (
                                            <p className="text-sm text-slate-500">Sélectionnez un message</p>
                                        ) : (
                                            <div className="flex flex-col h-full max-h-[75vh]">
                                                <div className="border-b pb-3 mb-3">
                                                    <p className="font-black text-lg">{selectedTicket.subject}</p>
                                                    <p className="text-sm text-slate-600">
                                                        De :{' '}
                                                        <strong>
                                                            {selectedTicket.userId?.fullName || selectedTicket.guestName}
                                                        </strong>{' '}
                                                        ({selectedTicket.userId?.email || selectedTicket.guestEmail})
                                                    </p>
                                                    <p className="text-xs text-slate-400 mt-1">
                                                        Origine :{' '}
                                                        {selectedTicket.source === 'contact'
                                                            ? 'Formulaire contact'
                                                            : selectedTicket.source === 'report'
                                                              ? 'Signalement problème'
                                                              : 'Support'}{' '}
                                                        · Statut : {selectedTicket.status}
                                                        {selectedTicket.userId ? ' · Compte lié' : ' · Invité'}
                                                    </p>
                                                </div>
                                                <div className="flex-1 overflow-y-auto space-y-3 mb-4">
                                                    {(selectedTicket.messages || []).map((m: any) => (
                                                        <div
                                                            key={m._id}
                                                            className={`rounded-xl px-3 py-2 text-sm ${
                                                                m.from === 'admin'
                                                                    ? 'bg-indigo-50 border border-indigo-100 ml-6'
                                                                    : 'bg-slate-50 border border-slate-100 mr-6'
                                                            }`}
                                                        >
                                                            <p className="text-[10px] font-bold uppercase text-slate-400">
                                                                {m.from === 'admin' ? 'Support' : m.authorName || 'User'} ·{' '}
                                                                {new Date(m.createdAt).toLocaleString('fr-FR')}
                                                            </p>
                                                            <p className="mt-1 whitespace-pre-wrap text-slate-800">{m.body}</p>
                                                        </div>
                                                    ))}
                                                </div>
                                                {selectedTicket.status !== 'closed' && (
                                                    <div className="space-y-2 border-t pt-3">
                                                        <textarea
                                                            className="w-full border rounded-xl px-3 py-2 text-sm min-h-[90px]"
                                                            placeholder="Votre réponse (notif côté utilisateur si compte)…"
                                                            value={replyBody}
                                                            onChange={(e) => setReplyBody(e.target.value)}
                                                        />
                                                        <div className="flex gap-2">
                                                            <Button
                                                                onClick={async () => {
                                                                    try {
                                                                        const updated = await adminService.replySupport(
                                                                            selectedTicket._id,
                                                                            replyBody
                                                                        );
                                                                        setSelectedTicket(updated);
                                                                        setReplyBody('');
                                                                        setTickets(await adminService.getSupportTickets());
                                                                        showToast('Réponse envoyée');
                                                                    } catch (e: any) {
                                                                        alert(e.message);
                                                                    }
                                                                }}
                                                                disabled={!replyBody.trim()}
                                                            >
                                                                Répondre
                                                            </Button>
                                                            <button
                                                                type="button"
                                                                className="text-xs font-bold border rounded-lg px-3"
                                                                onClick={async () => {
                                                                    const updated = await adminService.closeSupport(
                                                                        selectedTicket._id
                                                                    );
                                                                    setSelectedTicket(updated);
                                                                    setTickets(await adminService.getSupportTickets());
                                                                }}
                                                            >
                                                                Clôturer
                                                            </button>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </div>
                                </div>
                            )}

                            {tab === 'newsletter' && (
                                <div className="space-y-4">
                                    <div className="flex flex-wrap items-center justify-between gap-3">
                                        <div>
                                            <h2 className="text-lg font-black text-slate-900">Abonnés newsletter</h2>
                                            <p className="text-sm text-slate-500">
                                                Inscriptions depuis le pied de page du site.
                                            </p>
                                        </div>
                                        <button
                                            type="button"
                                            className="inline-flex items-center gap-1.5 text-xs font-bold border rounded-lg px-3 py-2 hover:bg-slate-50"
                                            onClick={() =>
                                                adminService
                                                    .getNewsletterSubscribers()
                                                    .then(setNewsletterSubs)
                                                    .catch((e: any) => alert(e.message))
                                            }
                                        >
                                            <RefreshCw className="w-3.5 h-3.5" />
                                            Actualiser
                                        </button>
                                    </div>
                                    <div className="flex flex-wrap gap-2">
                                        {(
                                            [
                                                { id: 'all', label: 'Tous' },
                                                { id: 'active', label: 'Actifs' },
                                                { id: 'unsubscribed', label: 'Désabonnés' },
                                            ] as const
                                        ).map((f) => (
                                            <button
                                                key={f.id}
                                                type="button"
                                                onClick={() => setNewsletterFilter(f.id)}
                                                className={`text-xs font-bold px-3 py-1.5 rounded-full border ${
                                                    newsletterFilter === f.id
                                                        ? 'bg-indigo-600 text-white border-indigo-600'
                                                        : 'bg-white text-slate-600 border-slate-200 hover:border-indigo-300'
                                                }`}
                                            >
                                                {f.label}
                                                <span className="ml-1 opacity-70">
                                                    (
                                                    {
                                                        newsletterSubs.filter((s) =>
                                                            f.id === 'all' ? true : s.status === f.id
                                                        ).length
                                                    }
                                                    )
                                                </span>
                                            </button>
                                        ))}
                                    </div>
                                    <div className="bg-white rounded-2xl border overflow-hidden">
                                        <table className="w-full text-sm">
                                            <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500">
                                                <tr>
                                                    <th className="px-4 py-3">Email</th>
                                                    <th className="px-4 py-3">Statut</th>
                                                    <th className="px-4 py-3">Source</th>
                                                    <th className="px-4 py-3">Date</th>
                                                    <th className="px-4 py-3 text-right">Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {newsletterSubs
                                                    .filter((s) =>
                                                        newsletterFilter === 'all'
                                                            ? true
                                                            : s.status === newsletterFilter
                                                    )
                                                    .map((s) => (
                                                        <tr key={s._id} className="border-t">
                                                            <td className="px-4 py-3 font-medium text-slate-900">
                                                                {s.email}
                                                            </td>
                                                            <td className="px-4 py-3">
                                                                <span
                                                                    className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded ${
                                                                        s.status === 'active'
                                                                            ? 'bg-emerald-100 text-emerald-700'
                                                                            : 'bg-slate-100 text-slate-600'
                                                                    }`}
                                                                >
                                                                    {s.status === 'active'
                                                                        ? 'Actif'
                                                                        : 'Désabonné'}
                                                                </span>
                                                            </td>
                                                            <td className="px-4 py-3 text-slate-500">
                                                                {s.source || '—'}
                                                            </td>
                                                            <td className="px-4 py-3 text-slate-500">
                                                                {new Date(
                                                                    s.createdAt
                                                                ).toLocaleString('fr-FR')}
                                                            </td>
                                                            <td className="px-4 py-3 text-right space-x-2">
                                                                {s.status === 'active' ? (
                                                                    <button
                                                                        type="button"
                                                                        className="text-xs font-bold text-amber-700 hover:underline"
                                                                        onClick={async () => {
                                                                            try {
                                                                                await adminService.updateNewsletterSubscriber(
                                                                                    s._id,
                                                                                    'unsubscribed'
                                                                                );
                                                                                setNewsletterSubs(
                                                                                    await adminService.getNewsletterSubscribers()
                                                                                );
                                                                                showToast('Désabonné');
                                                                            } catch (e: any) {
                                                                                alert(e.message);
                                                                            }
                                                                        }}
                                                                    >
                                                                        Désabonner
                                                                    </button>
                                                                ) : (
                                                                    <button
                                                                        type="button"
                                                                        className="text-xs font-bold text-emerald-700 hover:underline"
                                                                        onClick={async () => {
                                                                            try {
                                                                                await adminService.updateNewsletterSubscriber(
                                                                                    s._id,
                                                                                    'active'
                                                                                );
                                                                                setNewsletterSubs(
                                                                                    await adminService.getNewsletterSubscribers()
                                                                                );
                                                                                showToast('Réactivé');
                                                                            } catch (e: any) {
                                                                                alert(e.message);
                                                                            }
                                                                        }}
                                                                    >
                                                                        Réactiver
                                                                    </button>
                                                                )}
                                                                <button
                                                                    type="button"
                                                                    className="text-xs font-bold text-red-600 hover:underline"
                                                                    onClick={async () => {
                                                                        if (
                                                                            !window.confirm(
                                                                                `Supprimer ${s.email} ?`
                                                                            )
                                                                        ) {
                                                                            return;
                                                                        }
                                                                        try {
                                                                            await adminService.deleteNewsletterSubscriber(
                                                                                s._id
                                                                            );
                                                                            setNewsletterSubs(
                                                                                await adminService.getNewsletterSubscribers()
                                                                            );
                                                                            showToast('Supprimé');
                                                                        } catch (e: any) {
                                                                            alert(e.message);
                                                                        }
                                                                    }}
                                                                >
                                                                    Supprimer
                                                                </button>
                                                            </td>
                                                        </tr>
                                                    ))}
                                            </tbody>
                                        </table>
                                        {newsletterSubs.filter((s) =>
                                            newsletterFilter === 'all'
                                                ? true
                                                : s.status === newsletterFilter
                                        ).length === 0 && (
                                            <p className="p-6 text-sm text-slate-500 text-center">
                                                Aucun abonné pour le moment.
                                            </p>
                                        )}
                                    </div>
                                </div>
                            )}

                            {tab === 'settings' && (
                                <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
                                <div className="bg-white rounded-2xl border p-6 space-y-3">
                                    <p className="font-black">SMTP & site</p>
                                    {settings?.fromEnv && (
                                        <p className="text-xs text-amber-700 bg-amber-50 px-3 py-2 rounded-lg">
                                            Des variables d’environnement SMTP sont définies et ont priorité.
                                        </p>
                                    )}
                                    {(
                                        [
                                            ['siteName', 'Nom du site'],
                                            ['smtpHost', 'SMTP host'],
                                            ['smtpPort', 'Port'],
                                            ['smtpUser', 'User'],
                                            ['smtpPass', 'Password'],
                                            ['smtpFrom', 'From'],
                                        ] as const
                                    ).map(([key, label]) => (
                                        <div key={key}>
                                            <label className="text-xs font-bold text-slate-500">{label}</label>
                                            <input
                                                type={key === 'smtpPass' ? 'password' : 'text'}
                                                className="w-full border rounded-lg px-3 py-2 text-sm"
                                                placeholder={
                                                    key === 'smtpPass' && settings?.smtpPassSet ? '********' : ''
                                                }
                                                value={(settingsForm as any)[key]}
                                                onChange={(e) =>
                                                    setSettingsForm({
                                                        ...settingsForm,
                                                        [key]:
                                                            key === 'smtpPort'
                                                                ? Number(e.target.value) || 587
                                                                : e.target.value,
                                                    })
                                                }
                                            />
                                        </div>
                                    ))}
                                    <label className="flex items-center gap-2 text-sm">
                                        <input
                                            type="checkbox"
                                            checked={settingsForm.smtpSecure}
                                            onChange={(e) =>
                                                setSettingsForm({ ...settingsForm, smtpSecure: e.target.checked })
                                            }
                                        />
                                        Secure (SSL)
                                    </label>
                                    <div className="flex gap-2">
                                        <Button
                                            onClick={async () => {
                                                try {
                                                    const s = await adminService.updateSettings(settingsForm);
                                                    setSettings(s);
                                                    showToast('Réglages enregistrés');
                                                } catch (e: any) {
                                                    alert(e.message);
                                                }
                                            }}
                                        >
                                            Enregistrer
                                        </Button>
                                        <button
                                            type="button"
                                            className="text-sm font-bold border rounded-lg px-4"
                                            onClick={async () => {
                                                try {
                                                    await adminService.testEmail(user?.email);
                                                    showToast('Email de test envoyé');
                                                    setEmailLogs(await adminService.emailLogs());
                                                } catch (e: any) {
                                                    alert(e.message);
                                                }
                                            }}
                                        >
                                            Tester SMTP
                                        </button>
                                    </div>
                                    {settings && (
                                        <p className="text-xs text-slate-500">
                                            Configuré : {settings.smtpConfigured ? 'oui' : 'non'}
                                        </p>
                                    )}
                                </div>

                                <div className="bg-white rounded-2xl border p-6 space-y-4">
                                    <div>
                                        <p className="font-black">Moyens de paiement</p>
                                        <p className="text-xs text-slate-500 mt-1">
                                            Options affichées au client pour CV et lettres (partagées). Les prix se
                                            règlent séparément dans les menus CV / Lettres.
                                        </p>
                                    </div>
                                    {(
                                        [
                                            ['paylink', 'Lien Nafolo'],
                                            ['wave', 'Wave'],
                                            ['mtn', 'MTN Money'],
                                            ['orange', 'Orange Money'],
                                            ['moov', 'Moov Money'],
                                        ] as const
                                    ).map(([key, label]) => (
                                        <label
                                            key={key}
                                            className="flex items-center justify-between gap-3 border rounded-xl px-4 py-3"
                                        >
                                            <span className="font-semibold text-sm">{label}</span>
                                            <button
                                                type="button"
                                                role="switch"
                                                aria-checked={settingsForm.paymentMethods[key]}
                                                onClick={() =>
                                                    setSettingsForm({
                                                        ...settingsForm,
                                                        paymentMethods: {
                                                            ...settingsForm.paymentMethods,
                                                            [key]: !settingsForm.paymentMethods[key],
                                                        },
                                                    })
                                                }
                                                className={`relative w-12 h-7 rounded-full transition-colors ${
                                                    settingsForm.paymentMethods[key]
                                                        ? 'bg-emerald-500'
                                                        : 'bg-slate-300'
                                                }`}
                                            >
                                                <span
                                                    className={`absolute top-0.5 left-0.5 w-6 h-6 rounded-full bg-white shadow transition-transform ${
                                                        settingsForm.paymentMethods[key]
                                                            ? 'translate-x-5'
                                                            : ''
                                                    }`}
                                                />
                                            </button>
                                        </label>
                                    ))}
                                    <Button
                                        onClick={async () => {
                                            try {
                                                const s = await adminService.updateSettings({
                                                    paymentMethods: settingsForm.paymentMethods,
                                                });
                                                setSettings(s);
                                                if (s.paymentMethods) {
                                                    setSettingsForm((prev) => ({
                                                        ...prev,
                                                        paymentMethods: {
                                                            ...prev.paymentMethods,
                                                            ...s.paymentMethods,
                                                        },
                                                    }));
                                                }
                                                showToast('Moyens de paiement mis à jour');
                                            } catch (e: any) {
                                                alert(e.message);
                                            }
                                        }}
                                    >
                                        Enregistrer les moyens
                                    </Button>
                                </div>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </main>
        </div>
    );
};

export default AdminDashboard;
